import { defaultDarkTheme, defaultTheme } from "react-admin";
import { red } from "@mui/material/colors";
import { alpha, createTheme, SxProps, Theme } from "@mui/material";
import merge from "lodash/merge";

// See https://app.zeplin.io/styleguide/617a71c98b49c0bace1a9863/colors
export const autovioColors = {
  black: "#11130e",
  blue: "#007aff",
  blueUltraLight: "#d9ebff",
  green: "#74a089",
  greenLight: "#b9cfc4",
  greenUltraLight: "#e3ece7",
  greenDark: "#315340",
  greenMediumDark: "#5d806e",
  grey: "#888888",
  greyLight: "#d8d8d8",
  greyUltraLight: "#f2f2f2",
  orange: "#f39557",
  orangeMediumDark: "#c27746",
  orangeUltraLight: "#fdefe6",
  red: "#e87b7b",
  redLight: "#f6aaaa",
  redUltraLight: "#fae5e5",
  white: "#ffffff",
  yellow: "#f6ca00",
  yellowUltraLight: "#fdf4cc",
  violet: "#d483b7",
  violetUltraLight: "#f9eef5",
  borderGrey: "#e5e5e5",
};

export const fullWidthTabsStyle = {
  backgroundColor: "action.hover",
  alignItems: "center",
  borderRadius: "20px",
  minHeight: "0px",
  width: "100%",
};

export const cardTitleStyle = { fontWeight: "bold", fontSize: 15, mb: "15px" };

export const overviewCardStyle = {
  position: "relative",
  boxShadow: "none",
  backgroundColor: "background.paper",
  width: "100%",
  borderRadius: "16px",
  padding: "25px 30px",
  boxSizing: "border-box",
};

export const smallSelectInputStyle = {
  "& .MuiOutlinedInput-root": {
    minHeight: "37px",
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-input": {
    padding: "4.5px 12px",
  },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, 9px) scale(1)",
    "&.Mui-focused": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
    "&.MuiFormLabel-filled": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
  },
};

export const dotStyle = {
  width: 12,
  height: 12,
  borderRadius: 8,
  borderColor: "divider",
  borderStyle: "solid",
  borderWidth: "2.4px",
  boxShadow: "none",
};

export const dotStyle2 = {
  backgroundColor: autovioColors.orange,
  width: "20px",
  height: "20px",
  borderRadius: "50%",
};

export const menuPaperStyle = {
  minWidth: 220,
  borderRadius: 2,
  boxShadow: 3,
  "& .MuiMenu-list": {
    p: 1,
  },
};

export const menuItemStyle = {
  p: "10px 12px",
  borderRadius: 1,
  fontSize: "14px",
  fontWeight: "normal",
  color: "text.primary",
  "& .MuiListItemText-primary": {
    fontSize: "14px",
    fontWeight: "normal",
    lineHeight: 1.4,
  },
  "& .MuiListItemIcon-root": {
    minWidth: 30,
    color: "inherit",
  },
  "&:hover": {
    backgroundColor: "action.hover",
  },
};

export function statusMenuIconStyle(icon: "check" | "close"): SxProps<Theme> {
  return {
    width: 18,
    height: 18,
    padding: "2px",
    boxSizing: "border-box",
    borderRadius: "50%",
    color: "common.white",
    fill: "currentColor",
    backgroundColor: icon === "check" ? autovioColors.green : autovioColors.red,
  };
}

export const linkButtonStyle: SxProps<Theme> = {
  padding: "6px 16px 6px 16px",
  borderRadius: "20px",
  minHeight: "32px",
  height: "32px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "primary.ultraLight",
  },
};

export const backofficeTheme = {
  ...defaultTheme,
  shape: {
    borderRadius: 8,
  },
  palette: {
    background: {
      default: "#f5f5f5",
    },
    primary: {
      main: autovioColors.green,
      contrastText: autovioColors.white,
      light: autovioColors.greenLight,
      dark: autovioColors.greenDark,
      ultraLight: autovioColors.greenUltraLight,
      contrastTextUltraLight: autovioColors.green,
    },
    secondary: {
      main: autovioColors.black,
    },
    error: red,
    contrastThreshold: 3,
    tonalOffset: 0.2,
    // Custom tokens ...
    progress: {
      main: autovioColors.green,
      light: autovioColors.greyUltraLight,
    },
    eventChangeBefore: {
      main: autovioColors.orange,
    },
    eventChangeAfter: {
      main: autovioColors.green,
    },
    destructiveAction: {
      main: autovioColors.red,
      light: autovioColors.redUltraLight,
    },
    studentLabels: {
      greenUltraLight: { background: autovioColors.greenUltraLight, text: autovioColors.green },
      greyUltraLight: { background: autovioColors.greyUltraLight, text: autovioColors.grey },
      orangeUltraLight: { background: autovioColors.orangeUltraLight, text: autovioColors.orange },
      redUltraLight: { background: autovioColors.redUltraLight, text: autovioColors.red },
      violetUltraLight: { background: autovioColors.violetUltraLight, text: autovioColors.violet },
      blueUltraLight: { background: autovioColors.blueUltraLight, text: autovioColors.blue },
      yellowUltraLight: { background: autovioColors.yellowUltraLight, text: autovioColors.yellow },
    },
  },
  typography: {
    fontFamily: ["Poppins", "sans-serif"].join(","),
    button: {
      textTransform: "none" as any, // <-- to prevent yarn lint:tsc error
    },
    h6: {
      fontSize: "19px",
      fontWeight: "bold",
      lineHeight: 1.4,
    },
  },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          textWrap: "nowrap",
          backgroundColor: autovioColors.black,
          color: autovioColors.white,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          "&:disabled": {
            backgroundColor: autovioColors.greyLight,
            backgroundImage: "none",
            color: autovioColors.grey,
          },
        },
        outlined: {
          textWrap: "nowrap",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          boxShadow: "none",
          backgroundImage: "none",
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        focusHighlight: ({ theme }: { theme: Theme }) => ({ color: theme.palette.divider }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "16px",
          backgroundImage: "none",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: "none",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "24px 30px 10px 30px",
          fontSize: "19px",
          fontWeight: 600,
          marginRight: "50px" /* <-- to prevent the title from overlapping the DialogCloseButton */,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "0 30px 30px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "0 30px 30px 30px",
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        root: {
          "&:hover": { background: autovioColors.greyUltraLight },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 14,
          minWidth: 200,
          backgroundColor: autovioColors.white,
        },
        input: {
          "@media (max-width: 899px)": {
            fontSize: 16,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          cursor: "pointer",
          "&:hover": {
            textDecoration: "underline",
            textUnderlineOffset: "4px",
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: autovioColors.white,
            backgroundColor: autovioColors.green,
          },
          "&.Mui-selected:hover": {
            color: autovioColors.white,
            backgroundColor: autovioColors.green,
          },
        },
      },
    },
    MuiPagination: {
      defaultProps: {
        color: "primary",
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: "8px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: "6px 16px 6px 16px",
          minHeight: "32px !important",
          height: "32px !important",
          borderRadius: "16px",
          color: autovioColors.grey,
          textTransform: "none",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          "&.Mui-selected": {
            backgroundColor: autovioColors.green,
            color: autovioColors.white,
          },
          maxWidth: "none",
        },
      },
    },
    RaToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: autovioColors.white,
        },
      },
    },
    RaSelectArrayInput: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            minHeight: "37px",
            boxSizing: "border-box",
          },
          "& .MuiOutlinedInput-input": {
            padding: "4.5px 12px",
          },
        },
        chips: {
          marginTop: 0,
        },
      },
    },
  },
};

(backofficeTheme.components as any).MuiLoadingButton = backofficeTheme.components.MuiButton;

const academyColors = {
  orangeUltraLight: "#feeec0",
  orangeLight: "#fbd461",
  orange: "#f9bb02",
  orangeDark: "#b18501",
  black: "#11130e",
};

export type BackofficeBrand = "autovio" | "academy";
export type BackofficeColorScheme = "light" | "dark";

const darkDivider = "#3b433d";

const darkThemeOverrides = {
  palette: {
    mode: "dark",
    background: {
      default: "#151815",
      paper: "#202420",
    },
    text: {
      primary: "#f4f6f4",
      secondary: "#aeb8b0",
      disabled: "#747d76",
    },
    divider: darkDivider,
    progress: {
      light: alpha(autovioColors.green, 0.2),
    },
    secondary: {
      main: "#f4f6f4",
      contrastText: autovioColors.black,
    },
    action: {
      active: "#f4f6f4",
      hover: "rgba(255, 255, 255, 0.08)",
      selected: "rgba(255, 255, 255, 0.12)",
      disabled: "rgba(255, 255, 255, 0.38)",
      disabledBackground: "rgba(255, 255, 255, 0.12)",
    },
    destructiveAction: {
      light: "#442b2b",
    },
    studentLabels: {
      greenUltraLight: { background: "#21382b", text: autovioColors.greenLight },
      greyUltraLight: { background: "#303530", text: autovioColors.greyLight },
      orangeUltraLight: { background: "#493023", text: autovioColors.orange },
      redUltraLight: { background: "#442b2b", text: autovioColors.redLight },
      violetUltraLight: { background: "#402b39", text: "#e4a8cf" },
      blueUltraLight: { background: "#1d344d", text: "#7db7ff" },
      yellowUltraLight: { background: "#3d350f", text: autovioColors.yellow },
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          backgroundColor: "#f4f6f4",
          color: autovioColors.black,
          "&:hover": {
            backgroundColor: autovioColors.white,
          },
          "&:disabled": {
            backgroundColor: "rgba(255, 255, 255, 0.12)",
            color: "rgba(255, 255, 255, 0.38)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        focusHighlight: {
          color: darkDivider,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: "#202420",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: "#aeb8b0",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        },
      },
    },
    RaToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: "#202420",
        },
      },
    },
  },
};

function brandThemeOverrides(brand: BackofficeBrand, mode: BackofficeColorScheme) {
  if (brand === "autovio") {
    if (mode === "light") {
      return {};
    }
    const primaryMain = "#8bb9a1";
    return {
      palette: {
        primary: {
          main: primaryMain,
          contrastText: autovioColors.black,
          light: autovioColors.greenLight,
          dark: autovioColors.greenMediumDark,
          ultraLight: "#21382b",
          contrastTextUltraLight: autovioColors.greenLight,
        },
      },
      components: {
        MuiListItemButton: {
          styleOverrides: {
            root: {
              "&.Mui-selected, &.Mui-selected:hover": {
                color: autovioColors.black,
                backgroundColor: primaryMain,
              },
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              "&.Mui-selected": {
                color: autovioColors.black,
                backgroundColor: primaryMain,
              },
            },
          },
        },
      },
    };
  }

  return {
    palette: {
      primary: {
        main: academyColors.orange,
        contrastText: academyColors.black,
        light: academyColors.orangeLight,
        dark: academyColors.orangeDark,
        ultraLight: mode === "light" ? academyColors.orangeUltraLight : "#3d310d",
        contrastTextUltraLight: mode === "light" ? academyColors.black : academyColors.orangeLight,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          outlined: {
            border: `1px solid ${mode === "light" ? academyColors.black : academyColors.orange}`,
            color: mode === "light" ? academyColors.black : academyColors.orange,
          },
        },
      },
      MuiLink: {
        defaultProps: {
          color: "secondary",
        },
        styleOverrides: {
          root: {
            textDecoration: "underline",
            textDecorationColor: mode === "light" ? autovioColors.greyLight : darkDivider,
            textUnderlineOffset: "4px",
            "&:hover": {
              textDecorationColor: "inherit",
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              color: academyColors.black,
              backgroundColor: academyColors.orange,
            },
            "&.Mui-selected:hover": {
              color: academyColors.black,
              backgroundColor: academyColors.orange,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: academyColors.orange,
              color: academyColors.black,
            },
          },
        },
      },
    },
  };
}

export function createBackofficeTheme(brand: BackofficeBrand, mode: BackofficeColorScheme) {
  return createTheme(
    merge(
      {},
      mode === "light" ? defaultTheme : defaultDarkTheme,
      backofficeTheme,
      mode === "dark" ? darkThemeOverrides : {},
      brandThemeOverrides(brand, mode),
    ) as any,
  );
}

export const autovioTheme = createBackofficeTheme("autovio", "light");
export const autovioDarkTheme = createBackofficeTheme("autovio", "dark");
export const academyTheme = createBackofficeTheme("academy", "light");
export const academyDarkTheme = createBackofficeTheme("academy", "dark");

export const backofficeThemes = {
  autovio: { light: autovioTheme, dark: autovioDarkTheme },
  academy: { light: academyTheme, dark: academyDarkTheme },
};

export function backofficeBrandForDrivingSchool(drivingSchoolId?: string): BackofficeBrand {
  return drivingSchoolId === "e2e-test-driving-school" || drivingSchoolId === "28474154312" ? "academy" : "autovio";
}
